const READER_API_URL = import.meta.env.VITE_READER_API_URL || "http://localhost:5000";

class ReaderApiService {
  constructor() {
    this.baseUrl = READER_API_URL;
  }

  async request(endpoint, options = {}) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      if (text.trim().toLowerCase().startsWith("<!doctype")) {
        throw new Error("Server returned HTML instead of JSON. Check that the Reader API is running and VITE_READER_API_URL is correct.");
      }
      throw new Error("Server returned invalid response. Ensure the Reader API is running.");
    }

    if (!response.ok) {
      const err = new Error(data.error || "Request failed");
      err.status = response.status;
      err.data = data;
      throw err;
    }

    return data;
  }

  async getReaders() {
    return this.request("/readers");
  }

  async getAntennas(readerId) {
    const query = readerId ? `?readerId=${readerId}` : "";
    return this.request(`/antennas${query}`);
  }

  async getTags() {
    return this.request("/tags");
  }

  async assignReader(data) {
    return this.request("/assign-reader", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async assignAntenna(data) {
    return this.request("/assign-antenna", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteReader(readerId) {
    return this.request(`/readers/${readerId}`, {
      method: "DELETE",
    });
  }

  async deleteAntenna(readerId, antennaNumber) {
    return this.request(`/antennas/${readerId}/${antennaNumber}`, {
      method: "DELETE",
    });
  }

  async connectReader(readerId) {
    return this.request("/connect-reader", {
      method: "POST",
      body: JSON.stringify({ readerId }),
    });
  }

  async disconnectReader(readerId) {
    return this.request("/disconnect-reader", {
      method: "POST",
      body: JSON.stringify({ readerId }),
    });
  }

  async scanAntennas(readerId, antennas, duration = 3, powerOverrides = null) {
    const body = { readerId, antennas, duration };
    if (powerOverrides) body.powerOverrides = powerOverrides;
    return this.request("/scan-antennas", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async scanByAntenna(readerId, antennaNumber, duration = 3) {
    return this.request("/scan-by-antenna", {
      method: "POST",
      body: JSON.stringify({ readerId, antennaNumber, duration }),
    });
  }

  async scanByReader(readerId, duration = 3) {
    return this.request("/scan-by-reader", {
      method: "POST",
      body: JSON.stringify({ readerId, duration }),
    });
  }

  async startTheftMonitor(readerId) {
    return this.request("/start-theft-monitor", {
      method: "POST",
      body: JSON.stringify({ readerId }),
    });
  }

  async stopTheftMonitor(readerId) {
    return this.request("/stop-theft-monitor", {
      method: "POST",
      body: JSON.stringify({ readerId }),
    });
  }

  async getTheftMonitorStatus() {
    return this.request("/theft-monitor-status");
  }

  async getTheftEvents(readerId, alarmOnly = false) {
    const params = new URLSearchParams();
    if (readerId) params.set("readerId", readerId);
    if (alarmOnly) params.set("alarm", "true");
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.request(`/theft-events${query}`);
  }

  // Absolute URL for the Detection-log SSE stream.  The consumer passes
  // this directly to `new EventSource(...)` — fetch() can't be used for
  // SSE because it doesn't auto-reconnect and doesn't parse the wire
  // protocol.
  theftEventsStreamUrl() {
    return `${this.baseUrl}/theft-events/stream`;
  }

  // Absolute URL for the tag-state SSE stream.  Every time a tag's state
  // changes on the Python side (gate detection, transfer completion,
  // etc.) the matching tag doc is pushed through this pipe.  Pages that
  // show tag state (Dashboard, TagDetails, Inventory) open an
  // EventSource to this URL for instant updates independent of the
  // Node Socket.IO channel.
  tagsStreamUrl() {
    return `${this.baseUrl}/tags/stream`;
  }

  async clearTheftEvents() {
    return this.request("/theft-events", { method: "DELETE" });
  }

  async deleteTheftEventsBatch(ids) {
    return this.request("/theft-events/delete-batch", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  }

  async startContinuousScan(readerId, antennas, powerOverrides = null) {
    const body = { readerId, antennas };
    if (powerOverrides) body.powerOverrides = powerOverrides;
    return this.request("/start-continuous-scan", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async setAntennaPower(readerId, antennaNumber, power) {
    return this.request("/antenna-power", {
      method: "PUT",
      body: JSON.stringify({ readerId, antennaNumber, power }),
    });
  }

  async getReaderPower(readerId) {
    return this.request(`/reader-power?readerId=${readerId}`);
  }

  async stopContinuousScan(readerId) {
    return this.request("/stop-continuous-scan", {
      method: "POST",
      body: JSON.stringify({ readerId }),
    });
  }

  async getContinuousScanStatus(readerId) {
    const query = readerId ? `?readerId=${readerId}` : "";
    return this.request(`/continuous-scan-status${query}`);
  }

  // Frontend-liveness heartbeat. Call periodically while any continuous
  // scan is running so the backend watchdog keeps it alive. If the browser
  // tab closes or navigates away, heartbeats stop and the watchdog
  // auto-stops every continuous scan.
  async heartbeatScan() {
    return this.request("/heartbeat-scan", { method: "POST" });
  }

  // Stop a latched theft alarm. Omit readerId to clear every latched reader.
  async stopAlarm(readerId = null) {
    return this.request("/stop-alarm", {
      method: "POST",
      body: JSON.stringify(readerId ? { readerId } : {}),
    });
  }

  async getAlarmStatus() {
    return this.request("/alarm-status");
  }

  // Manually drive a GPO relay (wiring verification). gpo: 1|2|3|4, state: 0|1.
  async testGpo(readerId, gpo, state) {
    return this.request("/test-gpo", {
      method: "POST",
      body: JSON.stringify({ readerId, gpo, state }),
    });
  }

  // Desktop POS (USB HID)
  async getDesktopUsbPaths() {
    return this.request("/desktop-devices/usb-paths");
  }

  async getDesktopSerialPorts() {
    return this.request("/desktop-devices/serial-ports");
  }

  async getDesktopDevices() {
    return this.request("/desktop-devices");
  }

  async upsertDesktopDevice(deviceData) {
    return this.request("/desktop-devices", {
      method: "POST",
      body: JSON.stringify(deviceData),
    });
  }

  async deleteDesktopDevice(deviceId) {
    return this.request(`/desktop-devices/${deviceId}`, {
      method: "DELETE",
    });
  }

  async startDesktopPos(deviceId) {
    return this.request("/desktop-pos/start", {
      method: "POST",
      body: JSON.stringify({ deviceId }),
    });
  }

  async stopDesktopPos(deviceId) {
    return this.request("/desktop-pos/stop", {
      method: "POST",
      body: JSON.stringify({ deviceId }),
    });
  }

  async getDesktopPosStatus(deviceId = "") {
    const query = deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : "";
    return this.request(`/desktop-pos/status${query}`);
  }

  async payDesktopPos(deviceId, username) {
    return this.request("/desktop-pos/pay", {
      method: "POST",
      body: JSON.stringify({ deviceId, user: username }),
    });
  }

  async setDesktopPosPower(deviceId, power) {
    return this.request("/desktop-pos/power", {
      method: "PUT",
      body: JSON.stringify({ deviceId, power }),
    });
  }
}

export const readerApi = new ReaderApiService();
export default readerApi;
